import React, { useCallback, useState } from 'react'
import PrimeTable from '../../PrimeTable/PrimeTable';
import mainAxios from '../../../services/mainAxios';
import { useAppContext, usePersistedState } from '../../../AppContext';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faFileUpload, faPlus } from '@fortawesome/free-solid-svg-icons';
import CellInput from '../../global/CellInput';
import { Dialog } from 'primereact/dialog';
import EditAddSupplies from './EditAddContact';
import moment from 'moment';
import { contactTypesFn, getTcrList } from '../../../services/data';
import ImportSuppliers from '../Users/ImportSuppliers';
import currencies from '../../../services/currencies.json';
import ExportCsvPrime from '../../global/ExportCsvPrime';
import func from '../../../services/functions';
import "./contacts.scss";


const MySuppliers = ({ company: companyProp } = {}) => {
    const {
        user: [user],
        selectedCompany: [selectedCompany],
    } = useAppContext();

    // When rendered from the admin settings flow (e.g. /settings/contacts?companyId=159)
    // a specific company is passed in via props and must take precedence over the
    // navbar-selected company. Otherwise fall back to the navbar selection.
    const company =
        companyProp ??
        user.companiesRights.find(
            (company) => company.id === parseInt(selectedCompany)
        );
    const defaultFilters = {}

    const [filters, setFilters] = useState({
        params:{}
      });

    const [csvData, setCsvData] = useState([]);

    const [clients, setClients] = useState([]);
    const [open, setOpen] = useState({id:null,open:false});

    const defaultSortCriteria={
        field: "recordDate",
        direction: "DESC"
      } 

    const [tableRows, setTableRows] = useState({
        totalRows: 0,
        rowsPerPage: 10,
        currentPage: 1,
        sortCriteria:defaultSortCriteria

      });

    const { t,i18n } = useTranslation("translations");
    const tinTypes = [
        { label: "NUIS", value: "NUIS" },
        { label: "ID", value: "ID" },
        { label: "PASS", value: "PASS" },
        { label: "VAT", value: "VAT" },
        { label: "TAX", value: "TAX" },
        { label: "SOC", value: "SOC" },
    ];
    

    React.useEffect(() => {
        getFilteredResults();
      },[selectedCompany, company?.id]);

    const getFilteredResults = async ({
        updatedFilters,
        rowsPerPage = 10,
        currentPage = 1,
        sortCriteria = [ defaultSortCriteria ],
        exportCsv = false
      }={}) => {
       return await mainAxios
            .post("/apiEndpoint/search", {
                object: "GetConnections",
                value: null,
                params: updatedFilters?.params,
                company: company.id,
                pagination:exportCsv?null:{
                    pageSize: rowsPerPage,
                    pageNumber: currentPage,
                },
                sortCriteria
            })
            .then((res) => {
                if (res.data.status === true) {
                    if( exportCsv ) {
                        setCsvData(res.data.content);
                    }else{
                        setTableRows((tableRows) => ({
                            ...tableRows,
                            totalRows: res?.data?.totalSize,
                        }));
                        setClients(res.data.content);
                    }
                    return true
                }
                return false
            })
            .catch(()=>{
                return false
            })
    };

    const tcrList = React.useMemo(() => {
        return getTcrList(company,false)
    }, [selectedCompany, company?.id]);

  const contactTypeOptions = useCallback(()=>contactTypesFn(t),[t])

  const parseContact = useCallback((contact)=>((contact?.contactTypes ?? []).map(c=>c.type) ?? []).join(","),[])

    const columns = [
        {
            header: t(["home.actions"]),
            style: { minWidth: "100px", maxWidth: "100px" },
            body: (client) => (
                <>
                    <div
                        className="table-action-link"
                        onClick={() => { setOpen({id:client.id,open:true}) }}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                        {t("company.edit")}
                    </div>
                </>
            ),
            field:"actions",
            persisted: true,
            mobile: { priority: "action", group: "actions" },
        },
        {
            header: t("mySuppliers.contactName"),
            field: "name",
            filter: true,
            style: { minWidth: "180px", maxWidth: "180px" },
            sortable: true,
            default: true,
            mobile: { priority: 1, isTitle: true, group: "identity" },
        },
       {
            header:t("mySuppliers.contactType") ,
            field: "contactTypes.type",
            style: { minWidth: "200px", maxWidth: "300px" },
            mobile: { priority: 1, group: "identity" },
            filterType: "DROPDOWN",
            filterConfig:{
                options:contactTypeOptions(t),
                splitKey:false,
                optionValue: "type",
                optionLabel: "label",
            },  
            body:(contact)=>parseContact(contact),
            formattedExportValue:true,
            filter: true,
            default:true
        },
         {
            header: t("mySuppliers.nickName"),
            field: "nickname",
            style: { minWidth: "150px", maxWidth: "150px" },
            filter: true,
        },
        {
            header: t("mySuppliers.identityType"),
            field: "idType",
            filter: true,
            filterType: "DROPDOWN",
            filterConfig: {
                options: tinTypes,
                optionValue: "value",
                optionLabel: "label",
            },
            style: { minWidth: "180px", maxWidth: "180px" },
            default: true,
            mobile: { priority: 1, group: "identity" },
        },
        {
            header: t("mySuppliers.identificationValue"),
            field: "idValue",
            filter: true,
            style: { minWidth: "180px", maxWidth: "180px" },
            default: true,
            mobile: { priority: 1, group: "identity" },
        },
        {
            header: t("mySuppliers.profiscActive"),
            field: "active",
            style: { minWidth: "150px", maxWidth: "150px" },
            filterType:"TRISTATECHECKBOX",
            dataType:"boolean",
            filter: true,
            default:true,
            mobile: { priority: 1, isBadge: true, group: "status" },
        },
        {
            header: t("mySuppliers.city"),
            width: "125px",
            field: "town",
            style: { minWidth: "150px", maxWidth: "150px" },
            filter: true,
            default:true,
            mobile: { priority: 1, group: "location" },
        },
        {
            header: `${t("customers.country")}`,
            width: "80px",
            style: { minWidth: "150px", maxWidth: "150px" },
            field: "country",
            filter:true,
            default:true,
            mobile: { priority: 1, group: "location" },
        },
        {
            header: t("customers.address"),
            field: "address",
            filter: false,
            body: (queue) => <CellInput width={180} value={queue.address ?? ""} onFocus={(event) => event.target.select()} />,
            format: (value) => func.formatCell(value.address),
            default: true,
            mobile: { priority: 1, group: "location" },

        },
        {
            header: t("mySuppliers.phoneNr"),
            field: "phoneNumber",
            filter: false,
            style: { minWidth: "150px", maxWidth: "150px" },
            default:true,
            mobile: { priority: 1, group: "contact" },
        },
        {
            header: "Email",
            field: "email",
            filter: false,
            style: { minWidth: "150px", maxWidth: "150px" },
            default:true,
            mobile: { priority: 1, group: "contact" },
        },
        {
            header: t("mySuppliers.customerDefault"),
            field: "customerDefault",
            style: { minWidth: "180px", maxWidth: "180px" },
            filterType:"TRISTATECHECKBOX",
            dataType:"boolean",
            filter: true,
        },
        {
            header: t("mySuppliers.customerCode"),
            field: "customerCode",
            filter: true,
            style: { minWidth: "150px", maxWidth: "150px" },
            default:false
        },
        {
            header:t("mySuppliers.currencyDefault") ,
            field: "currencyDefault",
            style: { minWidth: "200px", maxWidth: "200px" },
            filterConfig:{
                options:currencies,
                optionValue: "value",
                optionLabel: "label",
            },  
            filterType: "DROPDOWN",
            filter: true,
        },
        {
            header: t("mySuppliers.vendorCode"),
            field: "vendorCode",
            style: { minWidth: "150px", maxWidth: "150px" },
            filter: true,
            default:true
        },
        {
            header: <div style={{fontSize:i18n.language=="alb"?13:14}}>{t("mySuppliers.defaultPurchaseUnit")}</div>,
            field: "purchaseUnit",
            filter: true,
            filterType: "DROPDOWN",
            filterConfig: {
                options: tcrList,
                optionValue: "value",
                optionLabel: "label",
            },
            style: { minWidth: "220px", maxWidth: "220px" },
            default: true,

        },
        {
            header: `${t("table.recordUser")}`,
            field: "recordUser",
            filter: true,
            style: { minWidth: "150px", maxWidth: "150px" },
            default: true,

        },
        {
            header: `${t("table.recordDate")}`,
            field: "recordDate",
            body: supplier => supplier.recordDate ? moment(supplier.recordDate).format("DD/MM/YYYY") : "",
            formattedExportValue:true,
            preventExcelFormat: true,
            filterType: "DATE",
            filterConfig:{
                field:"invoiceDate"
            },
            style: { minWidth: "180px", maxWidth: "180px" },
            default: true,
        },
    ];

    
    const [selectedColumns, setSelectedColumns] = usePersistedState(
    columns.map((col) => ({field:col.field,show:!!col.default,...(col?.persisted?{persisted:true}:{})})),
    "contactsColumns"
  );

    
    const [importSuppliersVisible, setImportSuppliersVisible] = useState(false);
    const clearSupplier = () => {
        setOpen({id:null,open:false})
    }

  

    const headerButtonsLeft = (
        <>
            <div className="prime-table-toolbar__add-btn" onClick={() => setOpen({id:null,open:true})}>
                <FontAwesomeIcon icon={faPlus} />
                {t("mySuppliers.addContact")}
            </div>
            <div
                className="prime-table-toolbar__secondary-btn"
                onClick={() => setImportSuppliersVisible(true)}
            >
                <FontAwesomeIcon icon={faFileUpload} />
                Import
            </div>
        </>
    );

    const headerIcons = (
        <ExportCsvPrime
            selectedColumns={selectedColumns}
            iconOnly
            tooltip="Export CSV"
            data={csvData}
            onClick={() => getFilteredResults({updatedFilters:filters, exportCsv: true })}
            columns={columns}
            excludedColumns={["actions"]}
        />
    );

    return (
        <div>
            <Dialog
                header={`${open.id ? t("mySuppliers.editContact") : t("mySuppliers.addContact")}`}
                visible={open.open}
                onHide={() => clearSupplier()}
                style={{ width: "900px",minHeight:"90%" }}
                breakpoints={{ "960px": "80vw", "641px": "100vw" }}
            >
                {open?.open &&<EditAddSupplies
                    onDataSuccess={()=>{
                        setOpen({id:null,open:false});
                        getFilteredResults({updatedFilters:filters, rowsPerPage:tableRows.rowsPerPage, currentPage:tableRows.currentPage})
                    }}
                    clearSupplier={clearSupplier}
                    tcrList={tcrList}
                    id={open?.id}
                    company={company}
                />}
            </Dialog>

            <PrimeTable
                value={clients}
                columns={columns}
                defaultFilters={defaultFilters}
                filters={[filters,setFilters]}
                onRemoteDataChange={(remoteData) => {
                    const updatedFilters = remoteData.filters ?? filters
                    const updatedTableData = remoteData.tableRows ?? tableRows
                    let { currentPage, rowsPerPage,sortCriteria } = updatedTableData
                    sortCriteria = sortCriteria ? [sortCriteria] : null
                    getFilteredResults({updatedFilters, rowsPerPage, currentPage,sortCriteria})
                }}
                responsiveLayout="scroll"
                tableRows={[tableRows, setTableRows]}
                defaultSortCriteria={defaultSortCriteria}
                gridKey="contactsColumns"
                selectedColumns={[selectedColumns, setSelectedColumns]}
                showSelectColumns
                showFilters={true}
                clearFilters={true}
                reorderableColumns={true}
                dataKey="id"
                headerButtonsLeft={headerButtonsLeft}
                headerIcons={headerIcons}
            />
            <ImportSuppliers
                visible={importSuppliersVisible}
                onHide={()=>setImportSuppliersVisible(false)}
                onSuccess={() => {setImportSuppliersVisible(false);getFilteredResults({updatedFilters:filters, rowsPerPage:tableRows.rowsPerPage, currentPage:tableRows.currentPage})}}
            />
        </div>
    )
}

export default MySuppliers